import { useEffect, useState } from 'react'
import { Alert, Input, Select } from 'antd'
import { BankOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { isHandledAuthError, municipalitiesApi } from '@/services'
import { privacyPolicySettingsApi } from '@/services/privacyPolicySettings'
import type { NoticeSettingItem } from '@/services/noticeSettings'
import { usePermission } from '@/hooks/usePermission'
import { useToast } from '@/widgets/common'

type MunicipalityOption = { id: number; name: string; code: string }

const MODE_KEY = 'privacy_policy_display_mode'
const TEXT_KEY = 'privacy_policy_text'
const MODE_LINK = 'リンク'
const MODE_TEXT = '文言記載'
const DEFAULT_PRIVACY_TEXT =
  '利用者の個人情報は、本サービスにおける業務以外の目的で使用することはありません。\n\n' +
  '利用者の個人情報は、本サービスの提供元である{自治体名}及び{当該自治体}から本サービスの運営等に業務委託を受けた事業者以外の第三者に提供することはありません。'

/**
 * プライバシーポリシー表示設定（検討事項 No.60）。
 *
 * 住民申出フォーム末尾の同意欄を、自治体ごとに「リンク」または「文言記載」へ切り替える。
 * 文言記載の本文はこの画面で編集できる。決定通知書設定と同方式（ADMIN=自自治体、SYSTEM_ADMIN は対象自治体を選択）。
 */
export default function PrivacyPolicySettingsMgmt() {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { isSystemAdmin } = usePermission()
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([])
  const [targetMunicipalityId, setTargetMunicipalityId] = useState<number | null>(null)
  const [items, setItems] = useState<NoticeSettingItem[]>([])
  const [textDraft, setTextDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    if (!isSystemAdmin) return
    municipalitiesApi
      .list()
      .then(({ data }) => setMunicipalities(data))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('自治体一覧の取得に失敗しました')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin])

  const needsSelection = isSystemAdmin && targetMunicipalityId == null
  const modeItem = items.find((it) => it.key === MODE_KEY)
  const textItem = items.find((it) => it.key === TEXT_KEY)
  const isTextMode = modeItem?.value === MODE_TEXT

  const load = () => {
    if (needsSelection) {
      setItems([])
      setTextDraft('')
      return
    }
    setLoading(true)
    const req = isSystemAdmin
      ? privacyPolicySettingsApi.listForManagement(targetMunicipalityId as number)
      : privacyPolicySettingsApi.listForMyMunicipality()
    req
      .then(({ data }) => {
        const next = data ?? []
        setItems(next)
        setTextDraft(next.find((it) => it.key === TEXT_KEY)?.value ?? '')
      })
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('プライバシーポリシー表示設定の取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin, targetMunicipalityId])

  const saveItem = async (key: string, value: string) => {
    if (savingKey != null) return
    setSavingKey(key)
    try {
      const payloadValue = key === TEXT_KEY && !value.trim() ? DEFAULT_PRIVACY_TEXT : value
      const { data } = isSystemAdmin
        ? await privacyPolicySettingsApi.saveForManagement(targetMunicipalityId as number, {
            key,
            value: payloadValue,
          })
        : await privacyPolicySettingsApi.saveForMyMunicipality({ key, value: payloadValue })
      setItems((prev) => prev.map((it) => (it.key === key ? data : it)))
      if (key === TEXT_KEY) setTextDraft(data.value)
      toast.success('保存しました')
    } catch (error) {
      if (!isHandledAuthError(error)) toast.error('保存に失敗しました')
      load()
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: 'プライバシーポリシー表示' },
        ]}
      />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <BackButton onClick={() => navigate('submenu-admin')} />

        <div className="mt-2 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex items-start gap-4 p-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-md shadow-rose-200">
              <SafetyCertificateOutlined style={{ fontSize: 22 }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">プライバシーポリシー表示</h1>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-500">
                住民申出フォーム末尾の同意欄を、自治体ごとに「リンク」または「文言記載」へ切り替えます。文言記載の本文はこの画面で編集できます。変更は即時に保存されます。
              </p>
            </div>
          </div>

          {isSystemAdmin ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3">
              <BankOutlined className="text-gray-400" />
              <span className="text-sm font-medium text-gray-600">対象自治体</span>
              <Select<number>
                style={{ minWidth: 300 }}
                placeholder="設定する自治体を選択"
                value={targetMunicipalityId ?? undefined}
                onChange={(v) => setTargetMunicipalityId(v ?? null)}
                showSearch
                optionFilterProp="label"
                options={municipalities.map((m) => ({ value: m.id, label: `${m.name}（${m.code}）` }))}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          {needsSelection ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                <BankOutlined style={{ fontSize: 28 }} />
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-700">設定する自治体を選択してください</p>
              <p className="mt-1 max-w-md text-xs leading-relaxed text-gray-400">
                システム管理者は自治体に属さないため、上の「対象自治体」から選ぶと、その自治体のプライバシーポリシー表示を編集できます。
              </p>
            </div>
          ) : loading ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">読み込み中...</div>
          ) : !modeItem ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">設定項目がありません。</div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-gray-700">{modeItem.label}</div>
                  <div className="text-xs text-gray-400">キー: {modeItem.key}</div>
                </div>
                <Select
                  value={modeItem.value}
                  style={{ minWidth: 160 }}
                  disabled={savingKey === MODE_KEY}
                  onChange={(v) => void saveItem(MODE_KEY, v)}
                  options={[
                    { value: MODE_LINK, label: MODE_LINK },
                    { value: MODE_TEXT, label: MODE_TEXT },
                  ]}
                />
              </div>
              <div className="px-6 py-4">
                <div className="mb-2">
                  <div className="text-sm font-medium text-gray-700">表示文言</div>
                  <div className="text-xs text-gray-400">キー: {TEXT_KEY}。文言記載のときに住民フォームへ表示します。</div>
                </div>
                <Input.TextArea
                  value={textDraft}
                  rows={8}
                  disabled={!isTextMode || savingKey === TEXT_KEY}
                  onChange={(e) => setTextDraft(e.target.value)}
                  onBlur={() => {
                    if (!isTextMode) return
                    if (textDraft !== (textItem?.value ?? '')) void saveItem(TEXT_KEY, textDraft)
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <Alert
          className="mt-4"
          type="info"
          showIcon
          message="リンクは各自治体のプライバシーポリシーURL（自治体管理で設定）を使います。文言記載は上の本文と「上記の内容を確認しました。」のチェックです。本文の {自治体名} と {当該自治体} は住民フォーム側で自治体名に置き換わります。"
        />
      </div>
    </div>
  )
}
